import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  UserPlus,
  Shield,
  MoreHorizontal,
  Mail,
  KeyRound,
  Pencil,
  UserX,
  Search,
  Loader2,
} from 'lucide-react'
import { usersApi } from '@/lib/api/users'
import { devicesApi } from '@/lib/api/devices'
import { useAuthStore } from '@/store/authStore'
import type { UserWithPermissions } from '@portal/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import UserForm from '@/components/features/users/UserForm'
import PermissionMatrix from '@/components/features/users/PermissionMatrix'
import PendingDeviceList from '@/components/features/users/PendingDeviceList'

const ROLE_LABELS: Record<string, { label: string; color: string }> = {
  ADMIN: { label: 'Admin', color: 'bg-purple-100 text-purple-700' },
  USER: { label: 'User', color: 'bg-slate-100 text-slate-700' },
}

export default function UsersPage() {
  const qc = useQueryClient()
  const currentUser = useAuthStore((s) => s.user)
  const isAdmin = currentUser?.role === 'ADMIN'
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [formOpen, setFormOpen] = useState(false)
  const [editUser, setEditUser] = useState<UserWithPermissions | null>(null)
  const [permsUser, setPermsUser] = useState<UserWithPermissions | null>(null)
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string; extra?: string } | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['users', page, search],
    queryFn: () => usersApi.list({ page, limit: 20, search: search || undefined }),
    select: (res) => (res.success && res.data ? res.data : { data: [], total: 0, page: 1, limit: 20 }),
  })

  const { data: pendingDevices } = useQuery({
    queryKey: ['pending-devices'],
    queryFn: () => devicesApi.getPending(),
    select: (res) => res.data ?? [],
    enabled: isAdmin,
  })

  const showToast = (type: 'success' | 'error', message: string, extra?: string) => {
    setToast({ type, message, extra })
    setTimeout(() => setToast(null), 6000)
  }

  const handleUserSaved = (user: UserWithPermissions, activationUrl?: string) => {
    qc.invalidateQueries({ queryKey: ['users'] })
    setFormOpen(false)
    setEditUser(null)
    if (activationUrl) {
      showToast('success', `User created. Activation link sent to ${user.email}.`, activationUrl)
    } else {
      showToast('success', 'User updated successfully.')
    }
  }

  const handlePermsSaved = (user: UserWithPermissions) => {
    qc.invalidateQueries({ queryKey: ['users'] })
    setPermsUser(null)
    showToast('success', `Permissions updated for ${user.name}.`)
  }

  const handleResendActivation = async (user: UserWithPermissions) => {
    try {
      const res = await usersApi.resendActivation(user.id)
      if (res.success) showToast('success', `Activation link resent to ${user.email}.`, res.data?.activationUrl)
      else showToast('error', res.error ?? 'Failed to resend activation')
    } catch {
      showToast('error', 'Failed to resend activation')
    }
  }

  const handleResetPassword = async (user: UserWithPermissions) => {
    try {
      const res = await usersApi.resetPassword(user.id)
      if (res.success) showToast('success', `Password reset link sent to ${user.email}.`, res.data?.activationUrl)
      else showToast('error', res.error ?? 'Failed to reset password')
    } catch {
      showToast('error', 'Failed to reset password')
    }
  }

  const handleApproveDevice = async (id: string) => {
    try {
      await devicesApi.approve(id)
      qc.invalidateQueries({ queryKey: ['pending-devices'] })
      showToast('success', 'Device approved.')
    } catch {
      showToast('error', 'Failed to approve device')
    }
  }

  const handleRejectDevice = async (id: string) => {
    try {
      await devicesApi.reject(id)
      qc.invalidateQueries({ queryKey: ['pending-devices'] })
      showToast('success', 'Device rejected.')
    } catch {
      showToast('error', 'Failed to reject device')
    }
  }

  const handleDeactivate = async (user: UserWithPermissions) => {
    try {
      const res = await usersApi.update(user.id, { isActive: false })
      if (res.success) {
        qc.invalidateQueries({ queryKey: ['users'] })
        showToast('success', `${user.name} deactivated. All sessions terminated.`)
      } else {
        showToast('error', res.error ?? 'Failed to deactivate')
      }
    } catch {
      showToast('error', 'Failed to deactivate user')
    }
  }

  const users = data?.data ?? []
  const total = data?.total ?? 0
  const totalPages = Math.ceil(total / 20)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage platform users, roles, and module access
          </p>
        </div>
        <Button onClick={() => setFormOpen(true)}>
          <UserPlus className="mr-2 h-4 w-4" />
          Add User
        </Button>
      </div>

      {/* Toast */}
      {toast && (
        <div
          className={`rounded-lg border p-4 text-sm ${
            toast.type === 'success'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          <p className="font-medium">{toast.message}</p>
          {toast.extra && (
            <p className="mt-1 font-mono text-xs break-all opacity-80">{toast.extra}</p>
          )}
        </div>
      )}

      {/* Pending device approvals — admin only */}
      {isAdmin && (pendingDevices?.length ?? 0) > 0 && (
        <PendingDeviceList
          devices={pendingDevices!}
          onApprove={handleApproveDevice}
          onReject={handleRejectDevice}
        />
      )}

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder="Search by name or email…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1) }}
        />
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40">
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Name</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Email</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Role</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Modules</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Limits</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Last Login</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">
                      No users found
                    </td>
                  </tr>
                ) : (
                  users.map((user) => {
                    const roleInfo = ROLE_LABELS[user.role] ?? { label: user.role, color: 'bg-gray-100 text-gray-700' }
                    const isActivated = !!user.lastLoginAt || user.isActive
                    return (
                      <tr key={user.id} className="border-b last:border-0 hover:bg-muted/20">
                        <td className="px-4 py-3 font-medium">{user.name}</td>
                        <td className="px-4 py-3 text-muted-foreground">{user.email}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${roleInfo.color}`}>
                            {roleInfo.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {!isActivated ? (
                            <Badge variant="secondary">Pending Activation</Badge>
                          ) : user.isActive ? (
                            <Badge variant="default" className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Active</Badge>
                          ) : (
                            <Badge variant="destructive">Inactive</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-muted-foreground">
                            {user.modules.length} of 6
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {user.dailyPaymentLimitPaise == null && user.transactionLimitPaise == null ? (
                            <span className="text-xs text-muted-foreground">—</span>
                          ) : (
                            <span className="text-xs text-foreground space-y-0.5 flex flex-col">
                              {user.dailyPaymentLimitPaise != null && (
                                <span>₹{(user.dailyPaymentLimitPaise / 100).toLocaleString('en-IN')}/day</span>
                              )}
                              {user.transactionLimitPaise != null && (
                                <span>₹{(user.transactionLimitPaise / 100).toLocaleString('en-IN')}/txn</span>
                              )}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">
                          {user.lastLoginAt
                            ? new Date(user.lastLoginAt).toLocaleDateString('en-IN')
                            : 'Never'}
                        </td>
                        <td className="px-4 py-3">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => { setEditUser(user); setFormOpen(true) }}>
                                <Pencil className="mr-2 h-3.5 w-3.5" />
                                Edit Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setPermsUser(user)}>
                                <Shield className="mr-2 h-3.5 w-3.5" />
                                Manage Permissions
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleResendActivation(user)}>
                                <Mail className="mr-2 h-3.5 w-3.5" />
                                Resend Activation
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleResetPassword(user)}>
                                <KeyRound className="mr-2 h-3.5 w-3.5" />
                                Reset Password
                              </DropdownMenuItem>
                              {user.isActive && (
                                <DropdownMenuItem
                                  onClick={() => handleDeactivate(user)}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <UserX className="mr-2 h-3.5 w-3.5" />
                                  Deactivate
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>{total} users total</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              Previous
            </Button>
            <span className="flex items-center px-2">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Modals */}
      <UserForm
        open={formOpen}
        user={editUser}
        onClose={() => { setFormOpen(false); setEditUser(null) }}
        onSaved={handleUserSaved}
      />

      {permsUser && (
        <PermissionMatrix
          open={!!permsUser}
          user={permsUser}
          onClose={() => setPermsUser(null)}
          onSaved={handlePermsSaved}
        />
      )}
    </div>
  )
}
